import React, { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MapPoint } from '../../utils';
import { MapPin } from 'lucide-react';
import * as Sentry from '@sentry/react';
import './CampMap.css';

// Set Mapbox access token from environment variable
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

export interface MapBounds {
  ne_lat: number;
  ne_lng: number;
  sw_lat: number;
  sw_lng: number;
  zoom: number;
}

interface CampMapProps {
  mapPoints?: MapPoint[];
  className?: string;
  hoveredOrganizerId?: number | null;
  onBoundsChange?: (bounds: MapBounds) => void;
}

const CampMap: React.FC<CampMapProps> = ({ mapPoints, className = '', hoveredOrganizerId = null, onBoundsChange }) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [markers, setMarkers] = useState<mapboxgl.Marker[]>([]);
  const [mapError, setMapError] = useState<string | null>(null);
  const isInitialSetup = useRef<boolean>(true);

  // Initialize map once on mount
  useEffect(() => {
    if (!mapContainer.current) return;

    const handleMapError = (error: any) => {
      console.error('Mapbox error:', error);
      Sentry.captureException(error || new Error('Unknown Mapbox error'), {
        tags: {
          component: 'CampMap',
          errorType: 'mapbox_runtime_error',
        }
      });
      if (error?.message?.includes('WebGL') || error?.message?.includes('webgl')) {
        setMapError('WebGL is not supported on your device. Please try using a different browser or device.');
      } else {
        setMapError('Map failed to initialize. Please refresh the page or try again later.');
      }
    };

    const handleBoundsChange = () => {
      if (!map.current || !onBoundsChange || isInitialSetup.current) return;

      const bounds = map.current.getBounds();
      if (!bounds) return;

      onBoundsChange({
        ne_lat: bounds.getNorthEast().lat,
        ne_lng: bounds.getNorthEast().lng,
        sw_lat: bounds.getSouthWest().lat,
        sw_lng: bounds.getSouthWest().lng,
        zoom: map.current.getZoom()
      });
    };

    try {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [-105.2747, 40.0190], // Center of USA
        zoom: 11,
        attributionControl: false
      });

      // Add navigation controls (zoom buttons only, no compass)
      map.current.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');

      // Add attribution control
      map.current.addControl(new mapboxgl.AttributionControl(), 'bottom-left');

      // Add error event listener
      map.current.on('error', (e) => {
        handleMapError(e.error);
      });

      // Listen for map move events (pan/zoom)
      map.current.on('moveend', handleBoundsChange);      

    } catch (error: any) {
      handleMapError(error);
    }

    return () => {
      if (map.current) {
        map.current.off('moveend', handleBoundsChange);
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  // Process mapPoints when they change
  useEffect(() => {
    if (!map.current) return;

    // Clear existing markers
    markers.forEach(marker => marker.remove());

    if (!mapPoints || mapPoints.length === 0) {
      setMarkers([]);
      return;
    }

    const bounds = new mapboxgl.LngLatBounds();
    const newMarkers: mapboxgl.Marker[] = [];

    // Create markers for each map point
    for (const point of mapPoints) {
      // Check if any organizer at this point has instant booking
      const hasInstantBooking = point.organizers.some(org => org.instant_booking === true);

      const mapboxCoordinates: [number, number] = [point.longitude, point.latitude];
      bounds.extend(mapboxCoordinates);

      // Create custom marker element with MapPin icon
      const markerElement = document.createElement('div');
      markerElement.className = 'custom-marker';
      markerElement.style.cursor = 'pointer';

      createRoot(markerElement).render(
        <MapPin
          size={30}
          strokeWidth="1"
          fill={hasInstantBooking ? "#10b981" : "#ffffff"}
          color={hasInstantBooking ? "#055050" : "#c2414d"}
          className="marker-icon"
        />
      );

      // Create marker
      const marker = new mapboxgl.Marker({
        element: markerElement,
        anchor: 'bottom'
      })
        .setLngLat(mapboxCoordinates)
        .addTo(map.current!);

      // Create popup with organizer names
      const popupContent = document.createElement('div');
      popupContent.innerHTML = `
        <div class="map-point-popup">
          <strong>${point.organizers.length} organizer${point.organizers.length > 1 ? 's' : ''}</strong>
          <ul style="margin: 8px 0 0 0; padding-left: 16px;">
            ${point.organizers.map(org => `<li>${org.name}</li>`).join('')}
          </ul>
        </div>
      `;

      const popup = new mapboxgl.Popup({
        closeButton: true,
        closeOnClick: true,
        maxWidth: '300px',
        focusAfterOpen: false,
        closeOnMove: true
      }).setDOMContent(popupContent);

      marker.setPopup(popup);
      newMarkers.push(marker);
    }

    setMarkers(newMarkers);

    if (mapPoints.length > 0 && isInitialSetup.current) {
      // Fit map to bounds of markers with padding
      map.current.fitBounds(bounds, { padding: 80, maxZoom: 15, duration: 1000 });
    }

    // After initial fitBounds, allow bounds change events
    setTimeout(() => {
      isInitialSetup.current = false;
    }, 3000);
  }, [mapPoints]);

  // Update map markers when hovered organizer id changes
  useEffect(() => {
    const highlightedPoint = mapPoints?.find(point => point.organizers.some(organizer => organizer.id === hoveredOrganizerId));
    markers.forEach(marker => {
      const markerLngLat = marker.getLngLat();
      if (markerLngLat.lng === highlightedPoint?.longitude && markerLngLat.lat === highlightedPoint.latitude) {
        const markerElement = marker.getElement();
        markerElement.classList.add("highlight");
      } else {
        const markerElement = marker.getElement();
        markerElement.classList.remove("highlight");
      }
    });
  }, [hoveredOrganizerId]);  

  if (mapError) {
    return (
      <div className={`camp-map-container ${className}`}>
        <div className="map-error">
          <p>{mapError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`camp-map-container ${className}`}>
      <div ref={mapContainer} className="camp-map" />
    </div>
  );
};

export default CampMap;
